import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Logo from '../images/shop_logo.png';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import Html5QrcodePlugin from './Html5QrcodePlugin';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import '../styles/Login.css';

Modal.setAppElement('#root');

const Products = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [update, setUpdate] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [newProduct, setNewProduct] = useState({
    name: '',
    barcode: '',
    category: '',
    price: '',
    costPrice: '',
    quantity: '',
    minStockLevel: '',
    description: '',
    taxRate: '',
    unit: 'pcs',
    isActive: true,
    expiryDate: ''
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [isFetchingProducts, setIsFetchingProducts] = useState(true);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel');
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');
  const inputRef = useRef(null);

  const productsCollectionRef = collection(db, "products");
  const categoriesCollectionRef = collection(db, "categories");
  const unitsCollectionRef = collection(db, "units");

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchUnits();
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && inputRef.current) {
        const inputValue = inputRef.current.value.trim();
        if (inputValue && !isNaN(inputValue)) {
          setSearchQuery(inputValue);
          inputRef.current.value = '';
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const filtered = products.filter((product) =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.barcode?.toString().includes(searchQuery) ||
      product.category?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredProducts(filtered);
  }, [searchQuery, products]);

  const fetchProducts = async () => {
    setIsFetchingProducts(true);
    try {
      const data = await getDocs(productsCollectionRef);
      const fetchedProducts = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setProducts(fetchedProducts);
    } catch (error) {
      setError("Failed to fetch products");
      setShowModal(true);
    } finally {
      setIsFetchingProducts(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await getDocs(categoriesCollectionRef);
      const fetchedCategories = data.docs.map((doc) => doc.data().name);
      setCategories(fetchedCategories);
    } catch (error) {
      setError("Failed to fetch categories");
      setShowModal(true);
    }
  };

  const fetchUnits = async () => {
    try {
      const data = await getDocs(unitsCollectionRef);
      const fetchedUnits = data.docs.map((doc) => doc.data().name);
      setUnits(fetchedUnits);
    } catch (error) {
      setError("Failed to fetch units");
      setShowModal(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProduct({ ...newProduct, [name]: value });
  };

  const handleNumberInputChange = (e) => {
    const { name, value } = e.target;
    setNewProduct({ ...newProduct, [name]: value === '' ? '' : parseFloat(value) || 0 });
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setNewProduct({ ...newProduct, [name]: value });
  };

  const resetForm = () => {
    setNewProduct({
      name: '',
      barcode: '',
      category: '',
      price: '',
      costPrice: '',
      quantity: '',
      minStockLevel: '',
      description: '',
      taxRate: '',
      unit: 'pcs',
      isActive: true,
      expiryDate: ''
    });
  };

  const handleAddProduct = async () => {
    if (!newProduct.name.trim()) {
      setError("Product name cannot be empty");
      setShowModal(true);
      return;
    }
    if (!newProduct.price || parseFloat(newProduct.price) <= 0) {
      setError("Price must be greater than 0");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    try {
      await addDoc(productsCollectionRef, {
        ...newProduct,
        price: parseFloat(newProduct.price) || 0,
        costPrice: parseFloat(newProduct.costPrice) || 0,
        quantity: parseInt(newProduct.quantity) || 0,
        minStockLevel: parseInt(newProduct.minStockLevel) || 0,
        taxRate: parseFloat(newProduct.taxRate) || 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      setSuccessMessage("Product added successfully");
      fetchProducts();
      resetForm();
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add product");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateProduct = async () => {
    setUpdate(true);
    try {
      const productDoc = doc(db, "products", editingProduct.id);
      await updateDoc(productDoc, {
        ...newProduct,
        price: parseFloat(newProduct.price) || 0,
        costPrice: parseFloat(newProduct.costPrice) || 0,
        quantity: parseInt(newProduct.quantity) || 0,
        minStockLevel: parseInt(newProduct.minStockLevel) || 0,
        taxRate: parseFloat(newProduct.taxRate) || 0,
        updatedAt: Timestamp.now()
      });
      setSuccessMessage("Product updated successfully");
      fetchProducts();
      setEditingProduct(null);
      resetForm();
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update product");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleFileChange = (e) => setFile(e.target.files[0]);
  const handleFileTypeChange = (e) => setFileType(e.target.value);

  const processBulkImport = async () => {
    if (!file) {
      setError("Please select a file first");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    setImportStatus('Processing file...');
    setImportProgress(0);

    try {
      let productsData = [];

      if (fileType === 'excel') {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        productsData = XLSX.utils.sheet_to_json(worksheet);
      } else {
        const text = await file.text();
        const result = Papa.parse(text, { header: true, skipEmptyLines: true });
        productsData = result.data;
      }

      if (productsData.length === 0) throw new Error("No product data found in the file");

      setImportStatus(`Importing ${productsData.length} products...`);

      let successCount = 0, errorCount = 0;

      for (let i = 0; i < productsData.length; i++) {
        try {
          const product = productsData[i];
          if (!product.name || !product.price || parseFloat(product.price) <= 0) {
            errorCount++;
            continue;
          }

          await addDoc(productsCollectionRef, {
            name: product.name || '',
            barcode: product.barcode || '',
            category: product.category || '',
            price: parseFloat(product.price) || 0,
            costPrice: parseFloat(product.costPrice) || 0,
            quantity: parseInt(product.quantity) || 0,
            minStockLevel: parseInt(product.minStockLevel) || 0,
            description: product.description || '',
            taxRate: parseFloat(product.taxRate) || 0,
            unit: product.unit || 'pcs',
            isActive: product.isActive !== undefined ? product.isActive : true,
            expiryDate: product.expiryDate || '',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          });
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Error importing product ${i + 1}:`, error);
        }
        setImportProgress(Math.round(((i + 1) / productsData.length) * 100));
      }

      setImportStatus(`Import complete: ${successCount} successful, ${errorCount} failed`);
      setSuccessMessage(`Bulk import completed: ${successCount} products added`);
      fetchProducts();
    } catch (error) {
      setError(`Bulk import failed: ${error.message}`);
      setShowModal(true);
    } finally {
      setUpdate(false);
      setFile(null);
      setTimeout(() => {
        setIsBulkImportModalOpen(false);
        setImportProgress(0);
        setImportStatus('');
      }, 2000);
    }
  };

  const handleEditProduct = (productToEdit) => {
    setEditingProduct(productToEdit);
    setNewProduct({
      name: productToEdit.name,
      barcode: productToEdit.barcode || '',
      category: productToEdit.category || '',
      price: productToEdit.price?.toString() || '',
      costPrice: productToEdit.costPrice?.toString() || '',
      quantity: productToEdit.quantity?.toString() || '',
      minStockLevel: productToEdit.minStockLevel?.toString() || '',
      description: productToEdit.description || '',
      taxRate: productToEdit.taxRate?.toString() || '',
      unit: productToEdit.unit || 'pcs',
      isActive: productToEdit.isActive !== false,
      expiryDate: productToEdit.expiryDate || ''
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteProduct = async (id) => {
    setUpdate(true);
    try {
      await deleteDoc(doc(db, "products", id));
      setSuccessMessage("Product deleted successfully");
      fetchProducts();
    } catch (error) {
      setError("Failed to delete product");
    } finally {
      setUpdate(false);
      setShowModal(true);
      setIsDeleteModalOpen(false);
    }
  };

  const openDeleteModal = (product) => {
    setCurrentProduct(product);
    setIsDeleteModalOpen(true);
  };

  const toggleProductStatus = async (product) => {
    setUpdate(true);
    try {
      await updateDoc(doc(db, "products", product.id), {
        isActive: !product.isActive,
        updatedAt: Timestamp.now()
      });
      setSuccessMessage(`Product ${!product.isActive ? 'activated' : 'deactivated'} successfully`);
      fetchProducts();
    } catch (error) {
      setError("Failed to update status");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const onNewScanResult = (decodedText) => {
    if (isAddModalOpen || isEditModalOpen) {
      setNewProduct(prev => ({ ...prev, barcode: decodedText }));
    } else if (isScanModalOpen) {
      setSearchQuery(decodedText);
    }
    setIsScanModalOpen(false);
  };

  const openViewModal = (product) => setViewProduct(product);

  if (loading) return <Loader text="LOADING" />;
  if (!userData) return <Loader text="READING" />;

  const allowedRoles = ['Admin', 'Supreme', 'Cashier'];

  return (
    <>
      {update && <Loader text="PROCESSING" />}
      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link"><i className="fa-solid fa-arrow-left size3"></i></Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Manage Products</h2>
            <Link to="#" className="home-link" onClick={fetchProducts}><i className="fa-solid fa-rotate-right size4"></i></Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">            
            <div className="row justify-center align-center gap-15 add-buttons">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Product</button>
              <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>Bulk Import</button>
            </div>

            <div className="row align-start justify-center search-scan-field">
              <input
                type="text"
                className="input-field1 input-field1-desktop"
                placeholder="Search by name, barcode, or category"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                ref={inputRef}
              />
              <button className="list-btn scan-btn" onClick={() => setIsScanModalOpen(true)}>
                <i className="fas fa-barcode"></i>
              </button>
            </div>

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {isFetchingProducts ? (
                  <p className="loading-text">Loading products ...</p>
                ) : filteredProducts.length === 0 ? (
                  <p className="loading-text">No products found</p>
                ) : (
                  filteredProducts.map((product) => (
                    <div key={product.id} className={`class-item class-item-products column ${!product.isActive ? 'inactive-product' : ''}`}>
                      <div>
                        <p className="column">
                          <span className="bold">{product.name} ({product.quantity})</span>
                          <span>₦ {product.price.toFixed(0)}</span>
                        </p>
                      </div>
                      <div className="row align-center justify-center gap-15 product-btns">
                        <button className={`list-btn ${product.isActive ? 'deactivate-btn' : 'activate-btn'}`} onClick={() => toggleProductStatus(product)}>
                          <i className={`fas font-white ${product.isActive ? 'fa-check' : 'fa-close'}`}></i>
                        </button>
                        <button className="list-btn" onClick={() => openViewModal(product)}><i className="fas fa-eye"></i></button>
                        <button className="list-btn" onClick={() => handleEditProduct(product)}><i className="fas fa-edit"></i></button>
                        <button className="list-btn delete-btn" onClick={() => openDeleteModal(product)}><i className="fas fa-trash-alt"></i></button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => { setError(""); setSuccessMessage(""); setShowModal(false); }}
        />
      )}

      {/* Add Product Modal */}
      <Modal isOpen={isAddModalOpen} onRequestClose={() => setIsAddModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Add Product</h2>
        <form className="product-form modal-scroll">
          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Product Name <span className="required">*</span></label>
              <input type="text" name="name" className="input-field" value={newProduct.name} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label className="input-label">Barcode</label>
              <div className="row" style={{ gap: '8px' }}>
                <input type="text" name="barcode" className="input-field" value={newProduct.barcode} onChange={handleInputChange} />
                <button type="button" className="list-btn font-white" onClick={() => setIsScanModalOpen(true)}>Scan</button>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Category</label>
              <select name="category" className="input-field" value={newProduct.category} onChange={handleInputChange}>
                <option value="">Select Category</option>
                {categories.map((c, i) => <option key={i} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Selling Price <span className="required">*</span></label>
              <input type="number" name="price" className="input-field" value={newProduct.price} onChange={handleNumberInputChange} min="0.01" step="0.01" required />
            </div>
            <div className="form-group">
              <label className="input-label">Cost Price</label>
              <input type="number" name="costPrice" className="input-field" value={newProduct.costPrice} onChange={handleNumberInputChange} min="0" step="0.01" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Quantity</label>
              <input type="number" name="quantity" className="input-field" value={newProduct.quantity} onChange={handleNumberInputChange} min="0" />
            </div>
            <div className="form-group">
              <label className="input-label">Min Stock Level</label>
              <input type="number" name="minStockLevel" className="input-field" value={newProduct.minStockLevel} onChange={handleNumberInputChange} min="0" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Unit</label>
              <select name="unit" className="input-field" value={newProduct.unit} onChange={handleInputChange}>
                <option value="">Select Unit</option>
                {units.map((u, i) => <option key={i} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="input-label">Tax Rate (%)</label>
              <input type="number" name="taxRate" className="input-field" value={newProduct.taxRate} onChange={handleNumberInputChange} min="0" max="100" step="0.1" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Expiry Date</label>
              <input type="date" name="expiryDate" className="input-field" value={newProduct.expiryDate} onChange={handleDateChange} />
            </div>
          </div>

          <div className="form-group">
            <label className="input-label">Description</label>
            <textarea name="description" className="input-field" rows="3" value={newProduct.description} onChange={handleInputChange} />
          </div>

          <div className="form-group">
            <label>
              <input type="checkbox" className="gold-tick" checked={newProduct.isActive} onChange={(e) => setNewProduct(prev => ({ ...prev, isActive: e.target.checked }))} />
              Active Product
            </label>
          </div>

          <div className="row justify-center gap-1 mt-20">
            <button type="button" className="login-button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button type="button" className="login-button" onClick={handleAddProduct}>Add Product</button>
          </div>
        </form>
      </Modal>

      {/* Edit Productobert Modal */}
      <Modal isOpen={isEditModalOpen} onRequestClose={() => { setIsEditModalOpen(false); resetForm(); setEditingProduct(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Edit Product</h2>
        <form className="product-form modal-scroll">
          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Product Name <span className="required">*</span></label>
              <input type="text" name="name" className="input-field" value={newProduct.name} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label className="input-label">Barcode</label>
              <div className="row" style={{ gap: '8px' }}>
                <input type="text" name="barcode" className="input-field" value={newProduct.barcode} onChange={handleInputChange} />
                <button type="button" className="list-btn font-white" onClick={() => setIsScanModalOpen(true)}>Scan</button>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Category</label>
              <select name="category" className="input-field" value={newProduct.category} onChange={handleInputChange}>
                <option value="">Select Category</option>
                {categories.map((c, i) => <option key={i} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Selling Price <span className="required">*</span></label>
              <input type="number" name="price" className="input-field" value={newProduct.price} onChange={handleNumberInputChange} min="0.01" step="0.01" required />
            </div>
            <div className="form-group">
              <label className="input-label">Cost Price</label>
              <input type="number" name="costPrice" className="input-field" value={newProduct.costPrice} onChange={handleNumberInputChange} min="0" step="0.01" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Quantity</label>
              <input type="number" name="quantity" className="input-field" value={newProduct.quantity} onChange={handleNumberInputChange} min="0" />
            </div>
            <div className="form-group">
              <label className="input-label">Min Stock Level</label>
              <input type="number" name="minStockLevel" className="input-field" value={newProduct.minStockLevel} onChange={handleNumberInputChange} min="0" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Unit</label>
              <select name="unit" className="input-field" value={newProduct.unit} onChange={handleInputChange}>
                <option value="">Select Unit</option>
                {units.map((u, i) => <option key={i} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="input-label">Tax Rate (%)</label>
              <input type="number" name="taxRate" className="input-field" value={newProduct.taxRate} onChange={handleNumberInputChange} min="0" max="100" step="0.1" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Expiry Date</label>
              <input type="date" name="expiryDate" className="input-field" value={newProduct.expiryDate} onChange={handleDateChange} />
            </div>
          </div>

          <div className="form-group">
            <label className="input-label">Description</label>
            <textarea name="description" className="input-field" rows="3" value={newProduct.description} onChange={handleInputChange} />
          </div>

          <div className="form-group">
            <label>
              <input type="checkbox" className="gold-tick" checked={newProduct.isActive} onChange={(e) => setNewProduct(prev => ({ ...prev, isActive: e.target.checked }))} />
              Active Product
            </label>
          </div>

          <div className="row justify-center gap-1 mt-20">
            <button type="button" className="login-button" onClick={() => { setIsEditModalOpen(false); resetForm(); setEditingProduct(null); }}>Cancel</button>
            <button type="button" className="login-button" onClick={handleUpdateProduct}>Update Product</button>
          </div>
        </form>
      </Modal>

      {/* View Product Modal */}
      <Modal isOpen={!!viewProduct} onRequestClose={() => setViewProduct(null)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Product Details</h2>
        {viewProduct && (
          <div className="product-details-modal">
            <p><strong>Name:</strong> {viewProduct.name}</p>
            <p><strong>Barcode:</strong> {viewProduct.barcode || 'N/A'}</p>
            <p><strong>Category:</strong> {viewProduct.category || 'N/A'}</p>
            <p><strong>Price:</strong> ₦ {viewProduct.price.toFixed(0)}</p>
            <p><strong>Cost Price:</strong> ₦ {viewProduct.costPrice.toFixed(0)}</p>
            <p><strong>Quantity:</strong> {viewProduct.quantity} {viewProduct.unit}</p>
            <p><strong>Min Stock Level:</strong> {viewProduct.minStockLevel}</p>
            <p><strong>Tax Rate:</strong> {viewProduct.taxRate}%</p>
            <p><strong>Expiry Date:</strong> {viewProduct.expiryDate || 'N/A'}</p>
            <p><strong>Description:</strong> {viewProduct.description || 'N/A'}</p>
            <p><strong>Status:</strong> {viewProduct.isActive ? 'Active' : 'Inactive'}</p>
            <button className="login-button" onClick={() => setViewProduct(null)}>Close</button>
          </div>
        )}
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteModalOpen} onRequestClose={() => setIsDeleteModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Delete Product</h2>
        {currentProduct && (
          <div>
            <p>Are you sure you want to delete <strong>{currentProduct.name}</strong>?</p>
            <p>This action cannot be undone.</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" type="button" onClick={() => handleDeleteProduct(currentProduct.id)}>Delete</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Scan Modal */}
      <Modal isOpen={isScanModalOpen} onRequestClose={() => setIsScanModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Scan Barcode</h2>
        <Html5QrcodePlugin fps={10} qrbox={250} disableFlip={false} qrCodeSuccessCallback={onNewScanResult} autoStart={true} />
        <button className="login-button" onClick={() => setIsScanModalOpen(false)}>Close Scanner</button>
      </Modal>

      {/* Bulk Import Modal */}
      <Modal isOpen={isBulkImportModalOpen} onRequestClose={() => { setIsBulkImportModalOpen(false); setFile(null); setImportProgress(0); setImportStatus(''); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Bulk Import Products</h2>
        <div className="modal-form">
          <div style={{ marginBottom: '5px' }}>
            <label><input type="radio" name="fileType" value="excel" checked={fileType === 'excel'} onChange={handleFileTypeChange} /> Excel (.xlsx)</label>
            <label><input type="radio" name="fileType" value="csv" checked={fileType === 'csv'} onChange={handleFileTypeChange} /> CSV (.csv)</label>
          </div>
          <input type="file" accept={fileType === 'excel' ? '.xlsx, .xls' : '.csv'} onChange={handleFileChange} className="input-field1" />
          {importStatus && <div><p>{importStatus}</p>{importProgress > 0 && <div style={{ width: '100%', backgroundColor: '#ddd', borderRadius: '5px' }}><div style={{ width: `${importProgress}%`, height: '20px', backgroundColor: '#4CAF50', borderRadius: '5px', textAlign: 'center', lineHeight: '20px', color: 'white' }}>{importProgress}%</div></div>}</div>}
          <div className="row align-center justify-center gap-15">
            <button type="button" className="login-button caps" onClick={() => { setIsBulkImportModalOpen(false); setFile(null); setImportProgress(0); setImportStatus(''); }}>Cancel</button>
            <button type="button" className="login-button caps" onClick={processBulkImport} disabled={!file || update}>Import</button>
          </div>
          <div className="import-instructions">
            <h4>File Format Instructions:</h4>
            <p><strong>REQUIRED:</strong> name, price</p>
            <p><strong>OPTIONAL:</strong> barcode, category, costPrice, quantity, minStockLevel, description, taxRate, unit, isActive, expiryDate</p>
            <p>Use first row as headers</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Products;